<template>
  <div>
    <div class="m-2">
      <b-row>
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <label>Show</label>
          <v-select
            class="per-page-selector d-inline-block mx-50"
            :value="perPage"
            :options="perPageOptions"
            :clearable="false"
            @input="$emit('update:perPage', $event)"
          />
          <label>entries</label>
        </b-col>
        <b-col
          cols="12"
          md="6"
        >
          <div class="d-flex align-items-center justify-content-end">
            <b-form-input
              class="d-inline-block mr-1"
              placeholder="Search..."
              :value="searchQuery"
              @input="$emit('update:searchQuery', $event)"
            />
            <slot name="header-actions" />
          </div>
        </b-col>
      </b-row>
    </div>

    <b-table
      ref="tableRef"
      class="position-relative"
      responsive
      primary-key="id"
      show-empty
      empty-text="No matching records found"
      :items="fetchData"
      :fields="columns"
      :sort-by.sync="localSortBy"
      :sort-desc.sync="localSortDesc"
      :busy="loading"
    >
      <template #table-busy>
        <b-skeleton-table
          :rows="perPage || 5"
          :columns="columns ? columns.length : 6"
          :table-props="{ bordered: true, striped: true }"
        />
      </template>

      <!-- Column: Number (with approval status + file indicator) -->
      <template #cell(transfer_number)="data">
        <div class="text-nowrap">
          <feather-icon
            v-if="data.item.transfer_rejected_by_rejector != null"
            icon="CircleIcon"
            class="danger"
          />
          <feather-icon
            v-else-if="data.item.transfer_confirmed_by_lab_manager_receiver == null"
            icon="CircleIcon"
            class="default"
          />
          <feather-icon
            v-else-if="data.item.transfer_received_by_receiver != null"
            icon="CircleIcon"
            class="success"
          />
          <feather-icon
            v-else-if="data.item.transfer_confirmed_by_lab_manager_sender != null"
            icon="CircleIcon"
            class="primary"
          />
          <feather-icon
            v-else
            icon="CircleIcon"
            class="warning"
          />
          <feather-icon
            v-if="data.item.transfer_has_files == true"
            icon="FileIcon"
          />
          &nbsp;
          <span class="align-text-top text-capitalize">{{ data.item.transfer_number }}</span>
          <file-status-badge :item="data.item" />
        </div>
      </template>

      <template #cell(transfer_branch_sender)="data">
        <span class="text-nowrap align-text-top text-capitalize">{{ data.item.sender.branch_name }}</span>
      </template>
      <template #cell(transfer_branch_receiver)="data">
        <span class="text-nowrap align-text-top text-capitalize">{{ data.item.receiver.branch_name }}</span>
      </template>
      <template #cell(transfer_notes)="data">
        <span class="text-nowrap align-text-top text-capitalize">{{ data.item.transfer_notes || '-' }}</span>
      </template>
      <template #cell(transfer_created_time)="data">
        {{ dateSimple(data.item.transfer_created_time) }}
      </template>

      <template #cell(Action)="data">
        <b-dropdown
          variant="link"
          no-caret
          :right="!$store.state.appConfig.isRTL"
        >
          <template #button-content>
            <feather-icon
              icon="MoreVerticalIcon"
              size="16"
              class="align-middle text-body"
            />
          </template>

          <b-dropdown-item :to="{ name: 'apps-transfers-details', params: { id: data.item.transfer_id } }">
            <feather-icon icon="ZoomInIcon" />
            <span class="align-middle ml-50">Detail</span>
          </b-dropdown-item>

          <b-dropdown-item
            v-if="canConfirmLabManager(data.item)"
            @click="$emit('confirm-lab', data.item)"
          >
            <feather-icon icon="CheckIcon" />
            <span class="align-middle ml-50">Confirm</span>
          </b-dropdown-item>

          <b-dropdown-item
            v-if="canConfirmLabManagerSender(data.item)"
            @click="$emit('confirm-lab-sender', data.item)"
          >
            <feather-icon icon="CheckIcon" />
            <span class="align-middle ml-50">Confirm</span>
          </b-dropdown-item>

          <b-dropdown-item
            v-if="canConfirmWarehouseSender(data.item)"
            @click="$emit('confirm-warehouse-sender', data.item)"
          >
            <feather-icon icon="CheckIcon" />
            <span class="align-middle ml-50">Confirm</span>
          </b-dropdown-item>

          <b-dropdown-item
            v-if="canReceiveWarehouse(data.item)"
            @click="$emit('receive-warehouse', data.item)"
          >
            <feather-icon icon="CheckIcon" />
            <span class="align-middle ml-50">Receive</span>
          </b-dropdown-item>

          <b-dropdown-item
            v-if="canConfirmRegional(data.item)"
            @click="$emit('confirm-regional', data.item)"
          >
            <feather-icon icon="CheckIcon" />
            <span class="align-middle ml-50">Confirm</span>
          </b-dropdown-item>

          <b-dropdown-item
            v-if="canConfirmDivision(data.item)"
            @click="$emit('confirm-division', data.item)"
          >
            <feather-icon icon="CheckIcon" />
            <span class="align-middle ml-50">Confirm</span>
          </b-dropdown-item>

          <b-dropdown-item
            v-if="canConfirmFaCoal(data.item)"
            @click="$emit('confirm-fa-coal', data.item)"
          >
            <feather-icon icon="CheckIcon" />
            <span class="align-middle ml-50">Confirm</span>
          </b-dropdown-item>

          <b-dropdown-item
            v-if="canReject(data.item)"
            @click="$emit('reject', data.item)"
          >
            <feather-icon icon="XIcon" />
            <span class="align-middle ml-50">Reject</span>
          </b-dropdown-item>

          <!-- ── Attachments (always visible) ── -->
          <b-dropdown-item @click="$emit('attachments', data.item.transfer_id)">
            <feather-icon icon="FileIcon" />
            <span class="align-middle ml-50">Attachments</span>
          </b-dropdown-item>

        </b-dropdown>
      </template>
    </b-table>

    <div class="mx-2 mb-2">
      <b-row>
        <b-col
          cols="12"
          sm="6"
          class="d-flex align-items-center justify-content-center justify-content-sm-start"
        >
          <span class="text-muted">Showing {{ metaData.from }} to {{ metaData.to }} of {{ metaData.of }} entries</span>
          <span style="padding-left: 10px;">
            <feather-icon icon="CircleIcon" class="default" /> = Just Created,
            <feather-icon icon="CircleIcon" class="warning" /> = In Approval,
            <feather-icon icon="CircleIcon" class="primary" /> = Released / Sent,
            <feather-icon icon="CircleIcon" class="success" /> = Finished,
            <feather-icon icon="CircleIcon" class="danger" /> = Rejected,
            <feather-icon icon="FileIcon" /> = Attachment
          </span>
        </b-col>
        <b-col
          cols="12"
          sm="6"
          class="d-flex align-items-center justify-content-center justify-content-sm-end"
        >
          <b-pagination
            class="mb-0 mt-1 mt-sm-0"
            first-number
            last-number
            prev-class="prev-item"
            next-class="next-item"
            :value="currentPage"
            :total-rows="totalRows"
            :per-page="perPage"
            @input="$emit('update:currentPage', $event)"
          >
            <template #prev-text>
              <feather-icon
icon="ChevronLeftIcon"
size="18"
/>
            </template>
            <template #next-text>
              <feather-icon
icon="ChevronRightIcon"
size="18"
/>
            </template>
          </b-pagination>
        </b-col>
      </b-row>
    </div>
  </div>
</template>

<script>
import {
  BRow, BCol, BFormInput, BTable, BDropdown, BDropdownItem, BPagination,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import moment from 'moment-timezone'
import FileStatusBadge from '../../components/FileStatusBadge.vue'

export default {
  components: {
    BRow, BCol, BFormInput, BTable, BDropdown, BDropdownItem, BPagination, vSelect, FileStatusBadge,
  },
  props: {
    fetchData: {
      type: Function,
      required: true,
    },
    columns: {
      type: Array,
      default: () => [],
    },
    perPage: {
      type: [Number, String],
      default: 10,
    },
    currentPage: {
      type: [Number, String],
      default: 1,
    },
    totalRows: {
      type: [Number, String],
      default: 0,
    },
    searchQuery: {
      type: String,
      default: '',
    },
    metaData: {
      type: Object,
      default: () => ({ from: 0, to: 0, of: 0 }),
    },
    sortBy: {
      type: String,
      default: '',
    },
    sortDesc: {
      type: Boolean,
      default: false,
    },
    userBranchId: {
      type: [Number, String],
      default: null,
    },
    permissions: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      perPageOptions: [10, 25, 50, 100],
    }
  },
  computed: {
    userRole() {
      return this.$cookies.get('UserRole')
    },
    localSortBy: {
      get() { return this.sortBy },
      set(val) { this.$emit('update:sortBy', val) },
    },
    localSortDesc: {
      get() { return this.sortDesc },
      set(val) { this.$emit('update:sortDesc', val) },
    },
  },
  methods: {
    refresh() {
      this.$refs.tableRef.refresh()
    },
    dateSimple(value) {
      return value ? moment.utc(value).tz('Asia/Jakarta').format('YYYY-MM-DD HH:mm:ss') : '-'
    },
    hasPerm(p) {
      return this.permissions.includes(p)
    },
    isSameBranch(id1, id2) {
      if (!id1 || !id2) return false
      return String(id1) === String(id2)
    },
    canConfirmLabManager(item) {
      return this.userRole === 'laboratory-manager'
        && item.transfer_confirmed_by_lab_manager_receiver === null
        && this.isSameBranch(item.transfer_branch_receiver, this.userBranchId)
    },
    canConfirmLabManagerSender(item) {
      return this.userRole === 'laboratory-manager'
        && item.transfer_confirmed_by_fa_coal_manager_sender === true
        && item.transfer_confirmed_by_lab_manager_sender === null
        && this.isSameBranch(item.sender.branch_id, this.userBranchId)
    },
    canConfirmWarehouseSender(item) {
      return this.userRole === 'warehouse-cabang'
        && item.transfer_confirmed_by_lab_manager_sender === true
        && (!item.transfer_sent_by_sender)
        && this.isSameBranch(item.sender.branch_id, this.userBranchId)
    },
    canReceiveWarehouse(item) {
      return this.userRole === 'warehouse-cabang'
        && item.transfer_sent_by_sender === true
        && (!item.transfer_received_by_receiver)
        && this.isSameBranch(item.receiver.branch_id, this.userBranchId)
    },
    canConfirmRegional(item) {
      return this.userRole === 'regional-manager'
        && item.transfer_confirmed_by_lab_manager_receiver === true
        && item.transfer_confirmed_by_regional_manager_sender === null
    },
    canConfirmDivision(item) {
      return this.userRole === 'division-head'
        && item.transfer_confirmed_by_regional_manager_sender === true
        && item.transfer_confirmed_by_division_head_sender === null
    },
    canConfirmFaCoal(item) {
      return this.userRole === 'fa-coal-manager'
        && item.transfer_confirmed_by_division_head_sender === true
        && item.transfer_confirmed_by_fa_coal_manager_sender === null
    },
    canReject(item) {
      return item.transfer_rejected_by_rejector === null && item.transfer_received_by_receiver === null
    },
  },
}
</script>